import { useParams } from 'react-router-dom';
import { Box, Chip, Skeleton, Svg, Text } from '@hopskipdrive/react-components';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';

import { ContactCard } from '../../../../shared/components/ContactCard';
import { CopyToClipboard } from '../../../../shared/components/CopyToClipboard';
import { colorPalette, colorTokens } from '../../../../shared/components/variables';
import { useRidersMustBeMetQuery } from '../../../hooks/useRidersMustBeMetQuery';
import { AIGCardContainer } from '../RiderDetailsPresenter.styles';

import { MBMCard } from './MustBeMetCard.styles';

export const MustBeMetCard = ({ riderSlug }) => {
  const defaultBadgeStyle = {
    display: 'inline-flex',
    minHeight: '35px',
    padding: '6px 8px',
    color: '#FFFFFF',
    border: 'none',
  };

  const badge = 'Must Be Met';
  const props = {
    icon: <Svg icon="handoff" width="16px" height="16px" style={{ color: '#FFFFFF' }} />,
    style: {
      ...defaultBadgeStyle,
      color: '#FFFFFF',
      background: colorPalette.red500,
    },
  };

  const { rideSlug } = useParams();
  const { data: mustBeMetResult, isFetching } = useRidersMustBeMetQuery({ rideSlug: rideSlug!, riderSlug: riderSlug });

  if (isFetching) {
    return <Skeleton width={143} height={35} />;
  }

  return mustBeMetResult?.data?.mustBeMetAtDropoff ? (
    <>
      {mustBeMetResult?.data?.tripAccountType === 'b2c' && <Chip key={badge} type="square" label={badge} {...props} />}
      {mustBeMetResult?.data?.tripAccountType === 'b2b' && (
        <Box width="100%">
          <MBMCard>
            <Box mb={3}>
              <Chip key={badge} type="square" label={badge} {...props} />
            </Box>
            <Box display="flex" alignItems="flex-start" flexDirection="column" gap="10px">
              <Box display="flex" flexDirection="column" gap="6px">
                <Text variant="subtitle1" textColor="secondaryText">
                  Protocol
                </Text>
                <Text variant="h6">{mustBeMetResult?.data?.protocolLabel}</Text>
              </Box>
              <Box display="flex" flexDirection="column" gap="6px" width="100%">
                <Text variant="subtitle1" textColor="secondaryText">
                  Authorized individual groups
                </Text>
                <AIGCardContainer>
                  {mustBeMetResult?.data?.authorizedContactGroups?.map((group, index) => (
                    <AIGCard
                      key={index}
                      name={group.name}
                      phoneNumber={group.phoneNumber}
                      verificationMethods={group.verificationMethods}
                    />
                  ))}
                </AIGCardContainer>
              </Box>
            </Box>
          </MBMCard>
        </Box>
      )}
    </>
  ) : null;
};

interface AIGCardProps {
  name: string;
  phoneNumber: string;
  verificationMethods: string[];
}

const AIGCard = ({ name, phoneNumber, verificationMethods }: AIGCardProps) => {
  return (
    <ContactCard color={colorTokens.error.main}>
      <ContactCard.Title>
        <Svg icon="handoff" width="24px" height="24px" style={{ color: colorPalette.red200 }} />
        <Text variant="h6" style={{ color: colorPalette.red200 }}>
          {name}
        </Text>
      </ContactCard.Title>
      <ContactCard.Content>
        <Box display="flex" gap="9px">
          {verificationMethods.map((method, index) => (
            <Box key={index} display="flex" flexDirection="row" gap="6px" ml={2}>
              <CheckRoundedIcon
                style={{
                  width: '23px',
                  height: '23px',
                  color: colorTokens.success.main,
                }}
              />
              <Text>{method}</Text>
            </Box>
          ))}
        </Box>
        <MustBeMetInfoContainer>
          <PhoneEnabledIcon style={{ color: colorTokens.text.secondary }} />
          <CopyToClipboard
            toastMessage="Phone Number Copied"
            text={phoneNumber}
            variant="h6"
            color={colorTokens.text.secondary}
          />
        </MustBeMetInfoContainer>
      </ContactCard.Content>
    </ContactCard>
  );
};

const MustBeMetInfoContainer = ({ children }) => {
  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap="9px" mb={3}>
      {children}
    </Box>
  );
};

export const MustBeMetCardSkeleton = () => (
  <Box width="100%">
    <MBMCard>
      <Box mb={3}>
        <Skeleton width="32px" />
      </Box>
      <Box display="flex" alignItems="flex-start" flexDirection="column" gap="10px">
        <Box display="flex" flexDirection="column" gap="6px">
          <Text variant="subtitle1" textColor="secondaryText">
            Protocol
          </Text>
          <Skeleton width="189px" />
        </Box>
        <Box display="flex" flexDirection="column" gap="6px" width="100%">
          <Text variant="subtitle1" textColor="secondaryText">
            Authorized individual groups
          </Text>
          <ContactCard color={colorTokens.error.main}>
            <ContactCard.Title>
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton width="189px" />
            </ContactCard.Title>
            <ContactCard.Content>
              <Box display="flex" gap="9px">
                <Box display="flex" flexDirection="row" gap="6px" ml={2}>
                  <Skeleton variant="circular" width={23} height={23} />
                  <Skeleton width="189px" />
                </Box>
              </Box>
              <MustBeMetInfoContainer>
                <Skeleton variant="circular" width={23} height={23} />
                <Skeleton width="189px" />
              </MustBeMetInfoContainer>
            </ContactCard.Content>
          </ContactCard>
        </Box>
      </Box>
    </MBMCard>
  </Box>
);
